import {
  FormEvent,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/_atoms/input";
import { Label } from "@/components/_atoms/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_atoms/table";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { GhgQualitySelect } from "@/domain/inventory/components/sector-tabs/ghg/ghg-quality-select";
import { useMunicipalPropertyYearMutations } from "@/domain/municipal-property/hooks/api/use-municipal-property-year-mutations";
import { useMunicipalPropertyConsumptionAutosave } from "@/domain/municipal-property/hooks/use-municipal-property-consumption-autosave";
import { getConsumptionCellKey } from "@/domain/municipal-property/models/get-consumption-cell-key";
import { MunicipalPropertyYearQuery } from "@/graphql/generated/graphql";
import { fromJoules, MUNICIPAL_PROPERTY_VALUE_CELL_POLICY, toGhgQuality } from "@ark/shared";

import { MunicipalPropertyConsumptionGrid } from "./municipal-property-consumption-grid";

type ValueCell = (typeof MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.cellList)[number];
type StoredValue = MunicipalPropertyYearQuery["municipalPropertyYear"]["valueList"][number];
interface CellGroup {
  key: string;
  cellList: readonly ValueCell[];
}
interface CellSection {
  key: string;
  title: string;
  groupList: CellGroup[];
}
const BUILDING_SCOPE_LIST: readonly number[] = [1, 2];

type Props = {
  year: MunicipalPropertyYearQuery["municipalPropertyYear"];
  canWrite: boolean;
  mutation: ReturnType<typeof useMunicipalPropertyYearMutations>;
  removeYearAction?: ReactNode;
  includeBuildingEnergy?: boolean;
  appearance?: "form" | "grid";
};


const MunicipalPropertyConsumptionFields = ({
  year,
  canWrite,
  mutation,
  removeYearAction,
  includeBuildingEnergy = true,
  appearance = "form",
}: Props): ReactElement => {
  const { t, i18n }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { t: translateInventory } = useTranslation("inventory");
  const legalCellList: readonly ValueCell[] = MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.cellList;
  const cellList: readonly ValueCell[] = useMemo(
    () => legalCellList.filter((cell) =>
      includeBuildingEnergy || cell.sectorKey === "WATER_CONSUMPTION"),
    [includeBuildingEnergy, legalCellList],
  );
  const sectionList: CellSection[] = [
    {
      key: "building-energy",
      title: t("register.property.buildingEnergy"),
      groupList: BUILDING_SCOPE_LIST.map(
        (scope: number): CellGroup => ({
          key: `scope-${scope}`,
          cellList: cellList.filter(
            (cell: ValueCell): boolean => cell.sectorKey === "MUNICIPAL_FACILITIES" && cell.scope === scope,
          ),
        }),
      ),
    },
    {
      key: "water",
      title: t("register.property.water"),
      groupList: [
        {
          key: "scope-3",
          cellList: cellList.filter((cell: ValueCell): boolean => cell.sectorKey === "WATER_CONSUMPTION"),
        },
      ],
    },
  ].filter((cellSection: CellSection): boolean => cellSection.groupList.some((group) => group.cellList.length > 0));

  const autosave = useMunicipalPropertyConsumptionAutosave({ year, canWrite, mutation, cellList });
  const { formRef, handleChange, handleBlur, conflictDialog } = autosave;

  return (
    <form
      key={year.id}
      ref={formRef}
      className={appearance === "grid" ? "flex h-full min-h-0 flex-col gap-4" : "flex h-full min-h-0 flex-col gap-4 px-6 pb-6"}
      onChange={handleChange}
      onBlur={handleBlur}
      onSubmit={(event: FormEvent<HTMLFormElement>): void => event.preventDefault()}
      onKeyDown={(event: KeyboardEvent<HTMLFormElement>): void => {
        if (appearance === "form" && event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault();
          event.target.blur();
        }
      }}
    >
      <dl className={`grid shrink-0 grid-cols-2 gap-4 rounded-md border p-3 text-sm ${appearance === "grid" ? "mx-6" : ""}`}>
        <div>
          <dt className="text-muted-foreground">{t("register.property.energyTotal")}</dt>
          <dd className="font-medium tabular-nums">
            <output aria-label={t("register.property.energyTotal")}>
              {fromJoules(year.energyTotalJ, "MWh").toLocaleString(i18n?.language)} {t("register.property.mwh")}
            </output>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t("register.property.waterTotal")}</dt>
          <dd className="font-medium tabular-nums">
            <output aria-label={t("register.property.waterTotal")}>
              {year.waterTotalM3.toLocaleString(i18n?.language)} {t("register.property.cubicMeters")}
            </output>
          </dd>
        </div>
      </dl>
      {appearance === "grid" ? <div className="min-h-0 flex-1">
        <MunicipalPropertyConsumptionGrid year={year} canWrite={canWrite} autosave={autosave} cellList={cellList} />
      </div> : <Table
        containerClassName="min-h-0 flex-1 border"
        className="border-collapse text-xs [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r [&_th]:h-8 [&_input]:h-8 [&_input]:text-xs [&_span]:text-xs"
      >
        <TableHeader className="sticky top-0 bg-muted [&_th]:font-semibold [&_th]:text-foreground">
          <TableRow className="hover:bg-muted">
            <TableHead>{t("register.property.columns.energySource")}</TableHead>
            <TableHead className="w-48 text-right">{t("register.property.columns.consumption")}</TableHead>
            <TableHead>{t("register.property.dataSource")}</TableHead>
            <TableHead>{translateInventory("rightAside.dataQuality")}</TableHead>
          </TableRow>
        </TableHeader>
        {sectionList.flatMap((cellSection: CellSection) => cellSection.groupList.map(
                (group: CellGroup): ReactElement => (
                  <TableBody key={`${cellSection.key}:${group.key}`}>
                    <TableRow className="bg-secondary hover:bg-secondary">
                      <TableHead scope="rowgroup" colSpan={4} className="font-semibold text-foreground">
                        {cellSection.key === "building-energy"
                          ? translateInventory("calculatedSector.scopeLabel", { scope: group.key.replace("scope-", "") })
                          : cellSection.title}
                      </TableHead>
                    </TableRow>
                    {group.cellList.map((cell: ValueCell): ReactElement => {
                      const key: string = getConsumptionCellKey(cell);
                      const value:
                        | MunicipalPropertyYearQuery["municipalPropertyYear"]["valueList"][number]
                        | undefined = year.valueList.find(
                        (item: StoredValue): boolean =>
                          item.sectorKey === cell.sectorKey &&
                          item.subsectorKey === (cell.subsectorKey ?? null) &&
                          item.energySourceKey === cell.energySourceKey,
                      );
                      const label: string = t(`register.property.energySource.${cell.energySourceKey}`);
                      return (
                        <TableRow key={key}>
                          <TableHead scope="row" className="bg-muted font-medium text-foreground">{label}</TableHead>
                          <TableCell className="p-0">
                            <Label className="sr-only" htmlFor={`amount:${key}`}>
                              {label} {cell.inputUnit}
                            </Label>
                            <div className="flex items-center">
                              <Input
                                className="h-10 min-w-0 rounded-none border-0 text-right tabular-nums shadow-none focus-visible:ring-inset"
                                id={`amount:${key}`}
                                name={`amount:${key}`}
                                type="number"
                                min="0"
                                step="any"
                                placeholder="0"
                                defaultValue={
                                  value
                                    ? cell.storageUnit === "J"
                                      ? fromJoules(value.amount, "MWh")
                                      : value.amount
                                    : ""
                                }
                                disabled={!canWrite}
                              />
                              <span className="shrink-0 pr-3 text-sm text-muted-foreground" aria-hidden="true">
                                {cell.inputUnit}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="p-0">
                            <Label className="sr-only" htmlFor={`source:${key}`}>
                              {label} {t("register.property.dataSource")}
                            </Label>
                            <Input
                              className="h-10 rounded-none border-0 shadow-none focus-visible:ring-inset"
                              id={`source:${key}`}
                              name={`source:${key}`}
                              maxLength={MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.dataSourceMaxLength}
                              defaultValue={value?.dataSource ?? ""}
                              disabled={!canWrite}
                            />
                          </TableCell>
                          <TableCell>
                            <GhgQualitySelect
                              value={toGhgQuality(autosave.draftMap.get(`${year.id}:${key}`)?.gpcQualityRating ?? value?.gpcQualityRating)}
                              disabled={!canWrite}
                              ariaLabel={`${label} ${translateInventory("rightAside.dataQuality")}`}
                              onValueChange={(quality) => autosave.handleCellUpdate({ rowId: key, columnId: "QUALITY", value: quality })}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                ),
              ))}
      </Table>}
      <div className={appearance === "grid" ? "mx-6 mb-6 flex flex-wrap justify-end gap-2" : "flex flex-wrap justify-end gap-2"}>{removeYearAction}</div>
      {conflictDialog}
    </form>
  );
};

export const MunicipalPropertyConsumptionForm = (props: Props): ReactElement => (
  <MunicipalPropertyConsumptionFields key={props.year.id} {...props} />
);
